/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { SagaIterator } from 'redux-saga';
import { all, call, delay, race, select, take, takeLatest } from 'redux-saga/effects';
import {
  API,
  CollectDataConfig,
  Ducks,
  EventTypeIds,
  ParentalRatingValue,
  TimeUtils,
  translate,
  User,
} from '@wit/vtv-x-foundation-lib';
import {
  getUA,
  osName,
  osVersion,
  isMobile,
  isEdge,
  isChrome,
  isChromium,
  isFirefox,
  isSafari,
  isEdgeChromium,
} from 'react-device-detect';
import { Selectors as AuthNSelectors } from 'core/store/ducks/playbackLicense/authN';
import { Selectors as ParentalControlSelectors } from 'core/store/ducks/parentalControl';
import AppConfig from '../../../services/AppConfig';
import * as Player from '../player';
import { PlaybackState } from '../player';

interface Navigator extends globalThis.Navigator {
  connection?: NetworkInformation | undefined;
  mozConnection?: NetworkInformation | undefined;
  webkitConnection?: NetworkInformation | undefined;
}

interface NetworkInformation {
  type?: string;
  effectiveType?: string;
}

export async function getNetworkType(): Promise<string | undefined> {
  const extendedNavigator: Navigator = navigator;
  const connection: NetworkInformation | undefined =
    extendedNavigator.connection || extendedNavigator.mozConnection || extendedNavigator.webkitConnection;
  if (connection) {
    return connection.type || (<NetworkInformation>connection).effectiveType;
  }

  return undefined;
}

export function getSoftwareVersion(): string | undefined {
  return `${osName} ${osVersion}`;
}

function* getStreaming(): SagaIterator<boolean> {
  const playbackState: PlaybackState = yield select(Player.Selectors.playbackState);
  return (
    playbackState !== PlaybackState.NO_STREAM &&
    playbackState !== PlaybackState.PLAYBACK_COMPLETED &&
    playbackState !== PlaybackState.PLAYBACK_ERROR &&
    playbackState !== PlaybackState.STOP &&
    playbackState !== PlaybackState.STREAM_ERROR
  );
}

function* getParentalRating(): SagaIterator<ParentalRatingValue | undefined> {
  const ratting: ParentalRatingValue | undefined = yield select(ParentalControlSelectors.getAgeRating);
  return ratting;
}

function* getEnvironment(): SagaIterator<string | undefined> {
  const api: API | undefined = yield select(Ducks.Dms.Selectors.getApi);
  return api?.tvpapi;
}

function* getAudioLevel(): SagaIterator<string> {
  const volume: number = yield select(Player.Selectors.volume);
  return volume.toString();
}

function* getBitrate(): SagaIterator<string> {
  const bitrate: number = yield select(Player.Selectors.bitrate);
  return bitrate.toString();
}

function getDrmSystem() {
  if (isMobile) {
    return '';
  }

  if (isChrome || isChromium || isFirefox || isEdgeChromium) {
    return 'Widevine';
  }

  if (isSafari) {
    return 'FairPlay';
  }

  if (isEdge) {
    return 'PlayReady';
  }

  return '';
}

function getStreamingProtocol() {
  if (isMobile) {
    return '';
  }

  return isSafari ? 'HLS' : 'DASH';
}

export function* sendRentActionEvent(): SagaIterator {
  const user: User | undefined = yield select(Ducks.Household.Selectors.data.getDefaultUser);
  const isAuthenticated = yield select(Ducks.Credentials.Selectors.isAuthenticated);
  const netType = yield call(getNetworkType);

  const technicalData: Ducks.TechnicalDataEvents.TechnicalData = {
    softwareVersion: getSoftwareVersion(),
    appVersion: AppConfig.env.VERSION,
    userAgent: getUA,
    environment: yield call(getEnvironment),
    networkType: netType,
    language: translate.getLanguage(),
    masterPinStatus: isAuthenticated ? user?.masterPin !== undefined : undefined,
    audioDescriptionStatus: isAuthenticated ? user?.accessibilityAudioDescription : undefined,
    subtitleStatus: isAuthenticated ? user?.accessibilitySubtitles : undefined,
    ip: undefined, // No longer supported by browsers
    currentAudioLevel: isAuthenticated ? yield call(getAudioLevel) : undefined,
    parentalRating: isAuthenticated ? yield call(getParentalRating) : undefined,
  };

  const isStreaming: boolean = yield call(getStreaming);
  const technicalDataWeb: Ducks.TechnicalDataEvents.TechnicalDataWeb = {
    streaming: isAuthenticated && isStreaming,
    httpReferer: document.referrer,
    adaptiveStreamingProtocol: isAuthenticated && isStreaming ? getStreamingProtocol() : undefined,
    streamingAbrProfile: isAuthenticated && isStreaming ? yield call(getBitrate) : undefined,
    playerType: 'HTML5',
    playerVersion: OTV.versions.product,
    nagraAuthNLastRequest: isAuthenticated ? yield select(AuthNSelectors.selectTokenLastUpdate) : undefined,
    multiDRMLicenseServerName: getDrmSystem(),
  };

  yield call(Ducks.TechnicalDataEvents.addTechnicalDataEvent, technicalData, technicalDataWeb);
}

function* startTechnicalDataCollection(): SagaIterator {
  const configs: CollectDataConfig | undefined = yield select(Ducks.AnalyticsConfigs.Selectors.getConfigs);
  if (configs) {
    const dataCollection = configs.dataCollection.find((el) => {
      return el.eventTypeIds.includes(EventTypeIds.TECHNICAL_DATA);
    });
    if (dataCollection) {
      const frequency = TimeUtils.secondsToMilliseconds(dataCollection.frequency);
      yield race([
        take(Ducks.Credentials.ActionTypes.SUCCESS),
        take(Ducks.Credentials.ActionTypes.REFRESH_SUCCESS),
        take(Ducks.Credentials.ActionTypes.SWITCH_USER_SUCCESS),
      ]);
      while (true) {
        yield call(sendRentActionEvent);
        yield delay(frequency);
      }
    }
  }
}

export function* saga(): SagaIterator {
  yield all([takeLatest(Ducks.AnalyticsEngineStarter.ActionTypes.RUNNING, startTechnicalDataCollection)]);
}
